import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import User from '@/models/User';
import useHiddenComponents from '@/data/hook/useHiddenComponent';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { scheduledLessonsQueryKeys } from '@/data/services/querykeys';
import { MANAGEMENT_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import InfinityList from '@/components/ui/custom/InfinityList';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { ScheduledLessonFilters } from '@/data/services/scheduledLessonsServices';
import { isTeacher } from '@/functions/auth';
import { getAuthorizedUnits } from '@/utils/getAuthorizedUnits';
import ManageScheduledLesson from '@/components/admin/calendar/ManageScheduledLesson/ManageScheduledLesson';
import HiddenWrapper, {
  HiddenOption,
  HiddenWrapperProps,
} from '@/components/ui/custom/HiddenWrapper/HiddenWrapper';
import useConfigContext from '@/data/hook/config';
import { EyeSlashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import TextArrowOrderingFilter from '@/components/filter/TextArrowOrderingFilter';
import TodayLessonsHeader from './TodayLessonsHeader';
import { LoadingIcon } from '@/components/icons';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { Fields, searchParamsToObject } from '@/utils/searchParamsToObject';

export type Props = Pick<HiddenWrapperProps, 'setRef'> & {
  user?: User;
};

export default function TodayLessonsComponent(props: Props) {
  const { user } = props;

  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { components } = useHiddenComponents();

  const component = components['todayLessons'];

  const configFields: Fields<ScheduledLessonFilters> = {
    teacher: { type: 'number' },
    isPendingReport: { type: 'string' },
    date: { type: 'string' },
    hasDone: { type: 'string' },
    ordering: { type: 'string' },
  };

  const initialValues: Partial<ScheduledLessonFilters> = {
    teacher: isTeacher(user?.userType) ? user?.id : undefined,
    unit: getAuthorizedUnits(user),
    isActive: true,
    hasDone: false,
    date: moment().format('YYYY-MM-DD'),
    ordering: 'datetime',
  };

  const noUrlFilter = searchParamsToObject<ScheduledLessonFilters>(
    initialValues,
    configFields,
  );

  const [filter, setFilter] = useState(noUrlFilter);

  const { hideComponent, hiddenComponents } = useConfigContext();

  const render = hiddenComponents?.todayLessons;

  const options: Array<HiddenOption> = [
    {
      icon: <EyeSlashIcon className="w-4" />,
      title: t('hideSection'),
      onClick: () => hideComponent({ todayLessons: false }),
    },
  ];

  const {
    results: scheduledLessons,
    isInitialLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    error,
    invalidate,
  } = useInfiniteService({
    ...scheduledLessonsQueryKeys.list({
      ...filter,
      pageSize: MANAGEMENT_PAGE_SIZE,
    })._ctx.infinity,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    retry: false,
  });

  return (
    <HiddenWrapper {...props} render={render}>
      <HiddenWrapper.Header
        title={component.title}
        icon={component.icon}
        header={
          <Tooltip text={t('refresh')} classNameContainer="absolute right-8">
            <IconButton
              icon={<ArrowPathIcon className="h-6 text-accent" />}
              onClick={() => invalidate()}
            />
          </Tooltip>
        }
        {...props}
      >
        <HiddenWrapper.Options options={options} />
      </HiddenWrapper.Header>
      <HiddenWrapper.Section>
        <aside className="flex justify-between pr-3 px-4">
          <TodayLessonsHeader filter={filter} onChange={setFilter} />
          <TextArrowOrderingFilter
            name="ordering"
            options={[
              {
                label: t('descendingTime'),
                value: '-datetime',
              },
              {
                label: t('increasingTime'),
                value: 'datetime',
              },
            ]}
            type="textArrowOrdering"
            setValue={(name, value) => setFilter({ ...filter, [name]: value })}
          />
        </aside>

        <InfinityList
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onReachEnd={fetchNextPage}
          className="gap-2 scrollbar-none h-[25rem] px-4"
          scroll
        >
          <ConditionalRenderer condition={isInitialLoading}>
            <div className="flex w-full h-full justify-center">
              <LoadingIcon className="text-primary/40 w-10" />
            </div>
          </ConditionalRenderer>

          <ConditionalRenderer condition={error}>
            <div className="flex w-full h-full justify-center">
              <Text format="rubik-400" text={getErrorMessage(error)} />
            </div>
          </ConditionalRenderer>

          <ConditionalRenderer
            condition={scheduledLessons.length && !isInitialLoading}
          >
            {scheduledLessons.map(scheduledLesson => (
              <ManageScheduledLesson
                state="view"
                key={scheduledLesson.id}
                scheduledLesson={scheduledLesson}
              />
            ))}
          </ConditionalRenderer>

          <ConditionalRenderer
            condition={!scheduledLessons.length && !isInitialLoading && !error}
          >
            <div className="flex w-full h-full justify-center pt-4">
              <Text
                text={`${t('noResults')} ¯\\_(ツ)_/¯`}
                className="overflow-hidden self-center text-primary"
              />
            </div>
          </ConditionalRenderer>
        </InfinityList>
      </HiddenWrapper.Section>
    </HiddenWrapper>
  );
}
